import React, { useContext, useEffect, useRef, useState } from 'react';

import { UserContext } from 'contexts/ConfigContext';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { PortfolioUrls } from 'config/urls';
import _ from 'lodash';

const errorMessage: string = 'Something went wrong!';

interface Props {
    queryKey: any;
    variables: any;
    queryConfig?: {
        enabled?: boolean;
    };
    setMediaTypesState: Function;
}

interface outputType {
    data: any;
    loading: boolean;
    error: any;
}

function useFetchMediaTypes({ queryKey, variables, queryConfig = {}, setMediaTypesState }: Props): outputType {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //initializing states
            setData(null);
            setError(null);
            setLoading(true);

            try {
                const data = await axios({
                    method: 'post',
                    url: PortfolioUrls.MediaTypesUrl,
                    data: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    //error handling
    if (!apiLoading && apiError && !error) {
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            if (payload?.length > 0) {
                const mediaTypes: any[] = [{ key: 'select all', label: 'Select All', selected: true }];

                payload.forEach((mt: string) => {
                    mediaTypes.push({ key: mt, label: mt, selected: true });
                });
                setMediaTypesState({
                    'Media Type': mediaTypes,
                });
            }
        } catch (error) {
            onFetchError(error);
            console.error(errorMessage, error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
    };
    return outputData;
}

export { useFetchMediaTypes };
